/**
 * Convert a data URL to a Blob object
 * @param {string} dataurl - Data URL string
 * @returns {Blob} - Blob object
 */
export const dataURLtoBlob = (dataurl) => {
  const arr = dataurl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

/**
 * Convert a file to base64 string
 * @param {File} file - File object
 * @returns {Promise<string>} - Base64 string
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("File không tồn tại"));
      return;
    }

    // Nếu là file ảnh và kích thước lớn hơn 1MB, resize trước khi chuyển thành base64
    if (file.type.startsWith("image/") && file.size > 1024 * 1024) {
      compressImage(file, {
        maxWidth: 1000,
        maxHeight: 1000,
        quality: 0.7,
      })
        .then((compressedBlob) => {
          // Chuyển blob đã compress thành base64
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(compressedBlob);
        })
        .catch(reject);
    } else {
      // Nếu không phải file ảnh hoặc kích thước nhỏ, chuyển trực tiếp
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    }
  });
};

/**
 * Nén một file ảnh thành kích thước nhỏ hơn
 * @param {File} file - File ảnh cần nén
 * @param {Object} options - Các tùy chọn nén
 * @param {number} options.maxWidth - Chiều rộng tối đa
 * @param {number} options.maxHeight - Chiều cao tối đa
 * @param {number} options.quality - Chất lượng ảnh sau khi nén (0-1)
 * @returns {Promise<Blob>} - Blob của ảnh đã nén
 */
export const compressImage = (file, options = {}) => {
  const { maxWidth = 1000, maxHeight = 1000, quality = 0.7 } = options;

  return new Promise((resolve, reject) => {
    // Tạo đối tượng Image để load ảnh
    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      // Tính toán kích thước mới dựa trên maxWidth và maxHeight
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
      }

      // Tạo canvas với kích thước mới
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      // Vẽ ảnh lên canvas
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      // Chuyển đổi canvas thành blob với chất lượng đã chỉ định
      canvas.toBlob(
        (blob) => {
          if (blob) {
            // Thêm thông tin về file gốc
            blob.name = file.name;
            resolve(blob);
          } else {
            reject(new Error("Không thể nén ảnh"));
          }
        },
        file.type,
        quality
      );

      // Giải phóng bộ nhớ
      URL.revokeObjectURL(img.src);
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error("Không thể tải ảnh để nén"));
    };
  });
};

/**
 * Ước tính kích thước (byte) của một chuỗi base64
 * @param {string} base64String - Chuỗi base64 cần kiểm tra
 * @returns {number} - Kích thước ước tính (byte)
 */
export const estimateBase64Size = (base64String) => {
  if (!base64String) return 0;

  // Loại bỏ prefix (data:image/jpeg;base64,)
  const base64 = base64String.split(",")[1] || base64String;

  // Công thức tính kích thước:
  // size = (string_length * 3/4) - (number_of_padding_chars)
  const padding = base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0;
  return Math.floor((base64.length * 3) / 4 - padding);
};

/**
 * Kiểm tra xem một chuỗi có phải là base64 hợp lệ hay không
 * @param {string} str - Chuỗi cần kiểm tra
 * @returns {boolean} - true nếu là base64 hợp lệ, false nếu không
 */
export const isValidBase64 = (str) => {
  if (!str || typeof str !== "string") return false;

  // Kiểm tra format data:image/xxx;base64,
  if (str.startsWith("data:")) {
    const matches = str.match(/^data:([A-Za-z-+\/]+);base64,(.+)$/);
    if (!matches || matches.length !== 3) return false;

    // Kiểm tra nếu mimeType không phải dạng image
    const mimeType = matches[1];
    if (!mimeType.startsWith("image/")) return false;

    // Kiểm tra nếu phần base64 rỗng
    const base64 = matches[2];
    if (!base64) return false;

    return true;
  }

  return false;
};

/**
 * Chuyển đổi URL thành base64
 * @param {string} url - URL của ảnh
 * @returns {Promise<string>} - Chuỗi base64 của ảnh
 */
export const urlToBase64 = (url) => {
  return new Promise(async (resolve, reject) => {
    try {
      // Fetch ảnh từ URL
      const response = await fetch(url);
      if (!response.ok) {
        reject(new Error(`Không thể tải ảnh từ URL: ${url}`));
        return;
      }

      // Chuyển đổi thành blob
      const blob = await response.blob();

      // Lấy mime type từ blob
      const mimeType = blob.type || "image/jpeg";

      // Chuyển đổi blob thành base64
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Xử lý dữ liệu ảnh để đảm bảo là base64 đúng định dạng
 * @param {any} imageData - Dữ liệu ảnh (có thể là URL, blob, base64, File)
 * @returns {Promise<string>} - Chuỗi base64 của ảnh
 */
export const processImageToBase64 = async (imageData) => {
  // Nếu đã là base64 hợp lệ
  if (typeof imageData === "string" && isValidBase64(imageData)) {
    return imageData;
  }

  // Nếu là URL (bao gồm cả blob: URL)
  if (
    typeof imageData === "string" &&
    (imageData.startsWith("http") || imageData.startsWith("blob:"))
  ) {
    return await urlToBase64(imageData);
  }

  // Nếu là File
  if (imageData instanceof File) {
    return await fileToBase64(imageData);
  }

  // Nếu là Blob
  if (imageData instanceof Blob) {
    const reader = new FileReader();
    return new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(imageData);
    });
  }

  throw new Error("Không thể xử lý dữ liệu ảnh không được hỗ trợ");
};
